import React, { useCallback } from 'react';
import {
  Button,
  ChevronRightIcon,
  Fact,
  LedgerHoverCard,
  LedgerRow,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import { LabourItemGroup } from '../../api/labourItemGroupsApi';

interface LabourGroupListItemProps {
  group: LabourItemGroup;
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  onItemClick: (id: string) => void;
  disabled?: boolean;
}

export const LabourGroupListItem = React.memo(function LabourGroupListItem({
  group,
  columns,
  collapse,
  onItemClick,
  disabled,
}: LabourGroupListItemProps) {
  const handleClick = useCallback(() => {
    if (!disabled) {
      onItemClick(group.id);
    }
  }, [disabled, onItemClick, group.id]);

  const previewText =
    group.itemNamePreview.length > 0
      ? group.itemNamePreview.join(', ') +
        (group.labourItemCount > group.itemNamePreview.length
          ? ` +${group.labourItemCount - group.itemNamePreview.length}`
          : '')
      : 'No items';
  const itemLabel = `${group.labourItemCount} item${group.labourItemCount === 1 ? '' : 's'}`;

  return (
    <LedgerRow
      columns={columns}
      collapse={collapse}
      title={group.name}
      cells={{ items: itemLabel, includes: previewText }}
      meta={`${itemLabel} · ${previewText}`}
      showValues={false}
      dimmed={disabled}
      hoverCard={
        <LedgerHoverCard
          title={group.name}
          kind="Labour group"
          onOpenDetails={handleClick}
          openDetailsLabel="Open group details"
        >
          <Fact label="Items">{group.labourItemCount}</Fact>
          <Fact label="Includes">{previewText}</Fact>
        </LedgerHoverCard>
      }
      actions={
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClick}
          disabled={disabled}
          aria-label={`Open ${group.name}`}
          className="size-8"
        >
          <ChevronRightIcon aria-hidden="true" className="size-4" />
        </Button>
      }
      onClick={disabled ? undefined : handleClick}
      aria-label={`Open labour group ${group.name}`}
      data-testid="labour-group-row"
    />
  );
});
